/* File: src\components\testing\countingSpan\CountingSpanInstructionsWrapper.js */
/* src/components/testing/countingSpan/CountingSpanInstructionsWrapper.js */
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../../contexts/ProfileContext';
import CountingSpanInstructions from './CountingSpanInstructions';
import { CST_TEST_TYPES } from '../../../constants/cstConstants';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Added
import { faTimes } from '@fortawesome/free-solid-svg-icons'; // Added


/**
 * Wrapper component for the Counting Span Instructions.
 * Renders the instructions and handles the callback when the user finishes
 * interacting with them (e.g., completes the practice steps within).
 * Marks the tutorial as complete and navigates back to the summary page.
 * Includes an Exit button.
 */
const CountingSpanInstructionsWrapper = () => {
    const navigate = useNavigate();
    // Need activeProfileId to mark tutorial complete for the correct profile
    // Need markTutorialCompleted function itself
    // Need isLoading to show loading spinner while profile context initializes
    const { activeProfileId, markTutorialCompleted, isLoading } = useProfile();

    /**
     * Callback function passed to CountingSpanInstructions or used by Exit button.
     * Triggered when the user completes the instruction/practice sequence or exits.
     * Marks the tutorial as seen for this profile and navigates back to the summary.
     */
    const handleTutorialComplete = useCallback(async () => {
        console.log("Counting Span Tutorial completed/exited via wrapper.");

        // Attempt to mark the tutorial as completed in Firestore
        if (activeProfileId) {
            // Use await if subsequent logic depends on completion, otherwise fire-and-forget is fine
            try {
                await markTutorialCompleted(activeProfileId, CST_TEST_TYPES.COUNTING_SPAN);
                console.log("Tutorial marked complete successfully.");
            } catch (error) {
                // Log error but don't block navigation
                console.error("Failed to mark tutorial complete in background:", error);
            }
        } else {
            // This shouldn't happen if the user got here via protected routes, but handle defensively
            console.warn("Cannot mark tutorial complete, no active profile ID found.");
        }

        // Navigate back to the summary page regardless of marking success
        navigate('/testing/counting-span', { replace: true }); // Use replace to prevent back button returning here

    }, [navigate, activeProfileId, markTutorialCompleted]); // Dependencies for the callback

    // Show loading spinner if the profile context (needed for activeProfileId) is loading
    if (isLoading) {
        return <LoadingSpinner message="Loading profile..." />;
    }

    // Render the actual instructions component, passing the completion callback
    return (
        // Container ensures positioning context for the absolute button
        // Removed min-h-screen and centering as SidebarLayout now handles page structure
        <div className="relative w-full bg-gray-100 p-4">
             <div className="w-full max-w-3xl mx-auto bg-white shadow-xl rounded-lg border border-gray-300 overflow-hidden p-4 sm:p-6">
                {/* --- MODIFICATION START: Exit Button --- */}
                <button
                    onClick={handleTutorialComplete}
                    className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full bg-gray-100 hover:bg-gray-200 z-10"
                    aria-label="Exit Tutorial"
                    title="Exit Tutorial"
                >
                    <FontAwesomeIcon icon={faTimes} className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                {/* --- MODIFICATION END --- */}

                {/*
                    Pass handleTutorialComplete to the onStartAssessment prop.
                    Even though the button inside might say "Start Assessment",
                    in this context, it signifies the end of the tutorial interaction.
                */}
                <CountingSpanInstructions onStartAssessment={handleTutorialComplete} />
             </div>
        </div>
    );
};

export default CountingSpanInstructionsWrapper;